import React from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Stack, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import currencyFormatter from '~/utils/currencyFormatter';

import { OrderStatusChip } from '../AllOrders/components/OrderStatusChip';
import { customerDataFormatter, orderCurrencyFormatter } from '../formatters';
import dateFormatter from '../formatters/dateFormatter';

function getPointsItem(order: App.Order): App.LoyaltyPointsItem | null {
  return order.loyalty_points_items?.[0] ?? null;
}

function getEffectivePointsOrderStatus(order: App.Order): string {
  if (order.status === 'cancelled') {
    return 'cancelled';
  }

  if (getPointsItem(order)?.status === 'cancelled') {
    return 'cancelled';
  }

  // A points purchase is always the sole item on an order, so any refund record
  // means the points purchase has been refunded — surface as cancelled.
  const refunds = order.refunds ?? [];
  if (refunds.length > 0 && order.status === 'completed') {
    return 'cancelled';
  }

  return order.status;
}

function pointsOrderStatusFormatter(params: GridRenderCellParams<App.Order>) {
  const order = params.row;
  const effectiveStatus = getEffectivePointsOrderStatus(order);

  return (
    <Stack direction="column">
      <Typography>{dateFormatter(order.created_at)}</Typography>
      <Stack width="fit-content">
        <OrderStatusChip orderStatus={effectiveStatus} />
      </Stack>
    </Stack>
  );
}

function pointPurchaseFormatter(params: GridRenderCellParams<App.Order>) {
  const item = getPointsItem(params.row);
  if (!item) {
    return <Typography>-</Typography>;
  }

  const totalPoints = item.offer_snapshot?.totalPoints ?? item.base_points_amount + item.bonus_points_amount;
  if (totalPoints > 0) {
    return <Typography>{totalPoints.toLocaleString()} points</Typography>;
  }

  return <Typography>{item.title || (item.total > 0 ? 'Points purchase' : '-')}</Typography>;
}

function pointsOrderTotalFormatter(params: GridRenderCellParams<App.Order>) {
  const order = params.row;
  const item = getPointsItem(order);
  const currencyCode = order.currency_code ?? 'AUD';
  const totalPaid = item?.total ?? order.total ?? 0;

  return (
    <Stack direction="column">
      <Typography>{currencyFormatter(currencyCode, totalPaid, 2)}</Typography>
    </Stack>
  );
}

function viewOrderFormatter(params: GridRenderCellParams<App.Order>) {
  return (
    <Button
      variant="text"
      size="small"
      href={`/purchases/${params.row.id}`}
      target="_blank"
      rel="noreferrer"
      startIcon={<OpenInNewIcon />}
      title="View order details in new window"
    >
      View Order
    </Button>
  );
}

export const pointsPurchaseColumns: Array<GridColDef> = [
  {
    field: 'customer',
    headerName: 'Customer',
    sortable: false,
    flex: 2,
    minWidth: 220,
    renderCell: customerDataFormatter,
    display: 'flex',
  },
  {
    field: 'order_status',
    headerName: 'Order Status',
    sortable: false,
    flex: 1.5,
    minWidth: 180,
    renderCell: pointsOrderStatusFormatter,
    display: 'flex',
  },
  {
    field: 'point_purchase',
    headerName: 'Point Purchase',
    sortable: false,
    flex: 1.5,
    minWidth: 180,
    renderCell: pointPurchaseFormatter,
    display: 'flex',
  },
  {
    field: 'currency',
    headerName: 'Currency',
    sortable: false,
    flex: 1,
    minWidth: 120,
    renderCell: orderCurrencyFormatter,
    display: 'flex',
  },
  {
    field: 'order_total',
    headerName: 'Order Total',
    sortable: false,
    flex: 1,
    minWidth: 140,
    renderCell: pointsOrderTotalFormatter,
    display: 'flex',
  },
  {
    field: 'view_order',
    headerName: 'View Order',
    sortable: false,
    flex: 1,
    minWidth: 160,
    renderCell: viewOrderFormatter,
    display: 'flex',
  },
];
